import { useState } from 'react';
import { errorMessage } from '../api';
import { confirmResetCode, sendResetCode } from '../auth';
import { Btn, Card, Field, Icon, Notice } from '../components/ui';

// Pantallas de acceso del sitio público, conmutables desde la landing y entre
// sí: Entrar (correo o celular), Crear cuenta (registro abierto: correo +
// contraseña ≥12 → código de verificación) y Recuperar (sendResetCode →
// confirmResetCode, mismo criterio de la app).

export type AuthMode = 'login' | 'register' | 'reset';
type ModeTarget = AuthMode | 'landing';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Celular al estilo del servidor: '+' inicial opcional y separadores típicos sobre 8–15 dígitos.
const phoneDigits = (value: string) => value.replace(/[\s().-]/g, '').replace(/^\+/, '');
const looksPhone = (value: string) => /^\d{8,15}$/.test(phoneDigits(value));

const TITLES: Record<AuthMode, { title: string; desc: string }> = {
  login: { title: 'Bienvenido de nuevo', desc: 'Entra con tu correo o tu celular.' },
  register: { title: 'Crea tu cuenta', desc: 'Te enviaremos un código de verificación a tu correo; después tu acceso queda en revisión del administrador.' },
  reset: { title: 'Recupera tu acceso', desc: 'Te enviaremos un código de 6 dígitos al correo de tu cuenta.' },
};

export function AuthView({ mode, onMode, onSignIn, onSignUp, busy, error, notice, dismiss }: {
  mode: AuthMode;
  onMode: (view: ModeTarget) => void;
  onSignIn: (identifier: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string, displayName: string) => Promise<void>;
  busy: boolean;
  error: string | null;
  notice: string | null;
  dismiss: () => void;
}) {
  const { title, desc } = TITLES[mode];
  return <main className="auth-wrap">
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    {notice && <Notice tone="info" onClose={dismiss}>{notice}</Notice>}
    <Card className="auth-card">
      <header className="auth-head">
        <h1 className="auth-title">{title}</h1>
        <p className="auth-desc">{desc}</p>
      </header>
      {mode === 'login' && <LoginForm onSignIn={onSignIn} busy={busy} />}
      {mode === 'register' && <RegisterForm onSignUp={onSignUp} busy={busy} />}
      {mode === 'reset' && <ResetForm onDone={() => onMode('login')} />}
      <div className="auth-links">
        {mode === 'login'
          ? <>
              <button type="button" className="link" onClick={() => onMode('register')}>Crear una cuenta</button>
              <button type="button" className="link" onClick={() => onMode('reset')}>Olvidé mi contraseña</button>
            </>
          : <button type="button" className="link" onClick={() => onMode('login')}>Ya tengo cuenta</button>}
      </div>
    </Card>
    <p className="tiny muted auth-foot">Portal — la operación diaria del chofer sigue en la app.</p>
  </main>;
}

/** Entrar: correo o celular (resolveIdentifier) + contraseña. */
function LoginForm({ onSignIn, busy }: { onSignIn: (id: string, pw: string) => Promise<void>; busy: boolean }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const typed = identifier.trim();
  const valid = (EMAIL_RE.test(typed) || looksPhone(typed)) && password.length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    try { await onSignIn(typed, password); } catch { /* el error ya lo muestra el banner */ }
  };

  return <form className="auth-form" onSubmit={event => { event.preventDefault(); void submit(); }}>
    <Field
      label="Correo o celular" value={identifier} onChange={event => setIdentifier(event.target.value)}
      placeholder="nombre@correo.com o 55 1234 5678" autoComplete="username" inputMode="email" autoCapitalize="none"
      hint="Si registraste tu celular en «Mi cuenta» de la app, también entra aquí."
    />
    <Field
      label="Contraseña" type={showPassword ? 'text' : 'password'} value={password}
      onChange={event => setPassword(event.target.value)} autoComplete="current-password" autoCapitalize="none"
    />
    <div className="row end">
      <button type="button" className="link" onClick={() => setShowPassword(!showPassword)}>
        {showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      </button>
    </div>
    <Btn busy={busy} disabled={!valid} type="submit">Entrar</Btn>
  </form>;
}

/** Crear cuenta: nombre + correo + contraseña (≥12). El alta crea la cuenta en
    Auth, fija el nombre y envía el código de verificación; la pantalla siguiente
    (Gate) pide el código y deja la solicitud en la cola del administrador. */
function RegisterForm({ onSignUp, busy }: { onSignUp: (email: string, pw: string, name: string) => Promise<void>; busy: boolean }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const typed = email.trim();
  const valid = EMAIL_RE.test(typed) && name.trim().length >= 2 && password.length >= 12;

  const submit = async () => {
    if (!valid || busy) return;
    try { await onSignUp(typed, password, name.trim()); } catch { /* el error ya lo muestra el banner */ }
  };

  return <form className="auth-form" onSubmit={event => { event.preventDefault(); void submit(); }}>
    <Field
      label="Tu nombre" value={name} onChange={event => setName(event.target.value)}
      placeholder="Como te conocen" autoComplete="name" autoCapitalize="words" maxLength={80}
    />
    <Field
      label="Correo electrónico" value={email} onChange={event => setEmail(event.target.value)}
      placeholder="nombre@correo.com" autoComplete="username" inputMode="email" autoCapitalize="none"
    />
    <Field
      label="Contraseña" type={showPassword ? 'text' : 'password'} value={password}
      onChange={event => setPassword(event.target.value)} autoComplete="new-password" autoCapitalize="none"
      hint={password && password.length < 12 ? `Mínimo 12 caracteres · lleva ${password.length}.` : 'Usa al menos 12 caracteres.'}
    />
    <div className="row end">
      <button type="button" className="link" onClick={() => setShowPassword(!showPassword)}>
        {showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      </button>
    </div>
    <Btn busy={busy} disabled={!valid} type="submit">Crear cuenta</Btn>
  </form>;
}

/** Recuperar: sendResetCode(email) decide el camino del despliegue — código
    de 6 dígitos → confirmResetCode(email, code, password), o enlace nativo de
    Auth (la nube no expone las acciones de código). Es silenciosa si el correo
    no existe: el aviso no revela si la cuenta está registrada. */
function ResetForm({ onDone }: { onDone(): void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState<'code' | 'link' | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const typed = email.trim();
  const emailValid = EMAIL_RE.test(typed);
  const codeValid = code.trim().length === 6 && password.length >= 12;

  const run = async (action: () => Promise<void>) => {
    setBusy(true); setMsg(null);
    try { await action(); } catch (e) { setMsg({ tone: 'danger', text: errorMessage(e) }); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!sent) {
      if (!emailValid || busy) return;
      await run(async () => {
        const mode = await sendResetCode(typed);
        setSent(mode);
        setMsg({ tone: 'info', text: mode === 'code'
          ? `Si la cuenta existe, enviamos un código de 6 dígitos a ${typed}.`
          : `Si la cuenta existe, enviamos un correo con el enlace para restablecer la contraseña a ${typed}.` });
      });
      return;
    }
    if (sent !== 'code' || !codeValid || busy) return;
    await run(async () => {
      await confirmResetCode(typed, code, password);
      setDone(true);
    });
  };

  if (done) {
    return <div className="auth-form">
      <div className="auth-done">
        <span className="feature-ic ok"><Icon name="check" size={22} /></span>
        <p className="small"><b>Contraseña actualizada.</b> Entra con tu nueva contraseña.</p>
      </div>
      <Btn onClick={onDone}>Entrar</Btn>
    </div>;
  }

  // Correo con enlace: no hay código que teclear — la nueva contraseña se
  // escribe en el formulario al que llega el enlace.
  if (sent === 'link') {
    return <div className="auth-form">
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <div className="auth-done">
        <span className="feature-ic"><Icon name="mail" size={22} /></span>
        <p className="small">Sigue el enlace del correo para elegir tu nueva contraseña (mínimo 12 caracteres) y después entra aquí.</p>
      </div>
      <Btn onClick={onDone}>Volver a entrar</Btn>
      <div className="row center">
        <button type="button" className="link" disabled={busy}
          onClick={() => void run(async () => { await sendResetCode(typed); setMsg({ tone: 'info', text: 'Si la cuenta existe, el correo va en camino. Revisa también correo no deseado.' }); })}>
          Reenviar correo
        </button>
      </div>
    </div>;
  }

  return <form className="auth-form" onSubmit={event => { event.preventDefault(); void submit(); }}>
    {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
    <Field
      label="Correo electrónico" value={email} onChange={event => setEmail(event.target.value)}
      placeholder="nombre@correo.com" autoComplete="username" inputMode="email" autoCapitalize="none"
      disabled={!!sent}
    />
    {sent === 'code' && <>
      <Field
        label="Código del correo" value={code} onChange={event => setCode(event.target.value)}
        placeholder="6 dígitos" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
      />
      <Field
        label="Nueva contraseña" type="password" value={password} onChange={event => setPassword(event.target.value)}
        autoComplete="new-password" autoCapitalize="none"
        hint={password && password.length < 12 ? `Mínimo 12 caracteres · lleva ${password.length}.` : 'Usa al menos 12 caracteres.'}
      />
    </>}
    <Btn busy={busy} disabled={sent === 'code' ? !codeValid : !emailValid} type="submit">
      {sent === 'code' ? 'Cambiar contraseña' : 'Enviar código'}
    </Btn>
    {sent === 'code' && <div className="row center">
      <button type="button" className="link" disabled={busy}
        onClick={() => void run(async () => { await sendResetCode(typed); setMsg({ tone: 'info', text: 'Código reenviado. Revisa también correo no deseado.' }); })}>
        Reenviar código
      </button>
    </div>}
  </form>;
}
